import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { BarChart3, Loader2 } from 'lucide-react';
import { validationApi } from '../api';

const META = {
  REJECTED_COOLING: { label: 'Cooling period', color: '#2E7D7A' },
  REJECTED_BRANCH_STOCK: { label: 'Branch stock', color: '#9C3D54' },
  REJECTED_HO_STOCK: { label: 'HO stock', color: '#5B4A8B' },
  VALIDATION_ERROR: { label: 'Validation error', color: '#D9A441' },
  DUPLICATE_LINE: { label: 'Duplicate line', color: '#8C8577' },
};

export default function RejectionTrendsPanel({ weeks = 8 }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  useEffect(() => { validationApi.trends(weeks).then(setData).catch(() => setHidden(true)); }, [weeks]);
  if (hidden) return null;

  const total = data ? data.weeks.reduce((n, w) => n + w.total, 0) : 0;
  return (
    <div className="fm-card p-5 mb-6" data-testid="rejection-trends-panel">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 size={16} className="text-[#9C3D54]" />
        <h3 className="text-[15px] font-medium">Rejection trends · last {weeks} weeks</h3>
        <span className="ml-auto text-[12px] text-[#7a6f5c]" data-testid="rejection-trends-total">{total} rejected line(s)</span>
      </div>
      <p className="text-[12px] text-[#7a6f5c] mb-4">Weekly (Mon–Sun, IST) Tester lines rejected at submission, by reason. Rising stock bars point to stock gaps.</p>
      {!data ? <div className="py-10 text-center"><Loader2 className="animate-spin inline" size={18} /></div> : (
        <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
          <div className="h-[260px]" data-testid="rejection-trends-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.weeks} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid stroke="#EFEADB" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#7a6f5c' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#7a6f5c' }} />
                <Tooltip labelFormatter={(l) => `Week of ${l}`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {data.reasons.map(r => <Bar key={r} dataKey={r} name={META[r]?.label || r} stackId="a" fill={META[r]?.color || '#999'} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <TopBranches branches={data.branches} />
        </div>
      )}
    </div>
  );
}

function TopBranches({ branches }) {
  return (
    <div data-testid="rejection-trends-branches">
      <div className="text-[12.5px] font-medium mb-2">Branches with most rejections</div>
      {branches.length === 0 ? <div className="text-[12.5px] text-[#4D7C5F]" data-testid="rejection-trends-empty">No rejections in this period.</div> : (
        <table className="w-full text-[12.5px]">
          <thead><tr className="text-left text-[11.5px] text-[#7a6f5c]">
            <th className="py-1 pr-2 font-medium">Branch</th><th className="py-1 pr-2 font-medium text-right">Total</th>
            <th className="py-1 pr-2 font-medium text-right" title="Branch stock">Br</th><th className="py-1 pr-2 font-medium text-right" title="HO stock">HO</th>
            <th className="py-1 font-medium text-right" title="Cooling period">Cool</th>
          </tr></thead>
          <tbody>
            {branches.map(b => (
              <tr key={b.branchCode} className="border-t border-[#EFEADB]" data-testid={`rejection-trends-branch-${b.branchCode}`}>
                <td className="py-1.5 pr-2">{b.branchName} <span className="font-mono text-[10.5px] text-[#7a6f5c]">{b.branchCode}</span></td>
                <td className="py-1.5 pr-2 text-right font-semibold">{b.total}</td>
                <td className="py-1.5 pr-2 text-right">{b.byReason.REJECTED_BRANCH_STOCK}</td>
                <td className="py-1.5 pr-2 text-right">{b.byReason.REJECTED_HO_STOCK}</td>
                <td className="py-1.5 text-right">{b.byReason.REJECTED_COOLING}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
